import { Button } from "components/Button";
import { useSession } from "hooks/useSession";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { iconArrowRight } from "assets/icons";
import { asParams } from "utils/asParams";
import { BaseRoutes } from "routes/BaseRoutes";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { CheckBox } from "components/CheckBox";
import { useSellersParams } from "hooks/usePagesParams/useSellersParams";
import { AuthConnectionErrorProps, useAuth } from "hooks/useAuth";

interface Property {
  name?: string,
  value?: string,
  action?: () => void,
}

interface Seller {
  sellerId: number,
  cnpj?: string,
  nickname?: string,
  grade?: number,
  seal?: string,
  active?: boolean,
  isConfi?: boolean,
  integratedSeller?: boolean,
}

interface SellerRowProps {
  sellerId: number,
  cnpj: string,
  nickname: string,
  grade: number,
  seal: string,
  active: JSX.Element | string,
  isConfi: JSX.Element | string,
  integratedSeller?: JSX.Element | string,
  actions: JSX.Element[],
}

export const useSellersTable = () => {
  const { params, setParams } = useSellersParams();
  const initialTableFilters:FilterProps = {
    'IsConfi': {
      label: 'Verificação',
      value: params?.IsConfi ?? '',
      options: [
        {label: 'Verificados', value: 'true'},
        {label: 'Não Verificados', value: 'false'}
      ],
    },
    'SealId': {
      label: 'Selo',
      value: params?.SealId ?? '',
      options: [
        {label: 'Saiba mais', value: '1'},
        {label: 'Boa', value: '2'},
        {label: 'Ótima', value: '3'},
        {label: 'Incrível', value: '4'},
        {label: 'Fantástica', value: '5'},
      ],
    }
  };
  const [properties, setProperties] = useState<Property[]>();
  const [sellers, setSellers] = useState<Seller[]>();
  const [sellerRows, setSellerRows] = useState<SellerRowProps[]>([]);
  const [tableFilters, setTableFilters] = useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState<string>(params?.KeyWord ?? '');
  const [pageNumber, setPageNumber] = useState<number>(params?.PageNumber ?? 1);
  const [sellersPerPage, setSellersPerPage] = useState<number>(params?.RowsPerPage ?? 10);
  const [sellersCount, setSellersCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const searchTextFormRef = useRef<any>(null);
  const { push } = useHistory();
  const { connectApi, cancelRequest } = useAuth();

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    }
  }, []);
  
  useEffect(() => {
    setPageNumber(1);
  }, [tableFilters, searchText, sellersPerPage])
  
  useEffect(() => {
    getSellers();
    setParams?.({
      ...params,
      IsConfi: tableFilters['IsConfi']?.value ?? '',
      SealId: tableFilters['SealId']?.value ?? '',
      KeyWord: searchText,
      PageNumber: pageNumber,
      RowsPerPage: sellersPerPage,
    });
  }, [tableFilters, searchText, pageNumber, sellersPerPage])

  const filtersAsParams = ():string => {
    var filters = [];
    for (let [key, value] of Object.entries(tableFilters)) {
      filters.push({key: key, value: value.value ?? ''});
    }
    filters.push({key: 'KeyWord', value: searchText});
    filters.push({key: 'PageNumber', value: pageNumber.toString()});
    filters.push({key: 'RowsPerPage', value: sellersPerPage.toString()});
    return asParams(filters);
  };

    const isConfiBadge = (isConfi: boolean) => {
    return (
      <CheckBox
        label={isConfi ? "Sim" : "Não"}
        name="isConfi"
        value=""
        readOnly={true}
        checked={true}
        checkedError={!isConfi}
      />
    )
  }

  const booleanBadge = (field: boolean, name: string) => {
    return (
      <CheckBox
        label={field ? "Sim" : "Não"}
        name={name}
        value=""
        readOnly={true}
        checked={true}
        checkedError={!field}
      />
    )
    }

  const rowActionButton = (route: string) => {
    return (
      <Button 
        label="" 
        size="small" 
        icon={iconArrowRight} 
        onlyIcon={true} 
        onClick={() => push(route)} 
      />
    )
  };

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setTableFilters({
      ...tableFilters,
      [eventName]: {
        ...tableFilters[eventName],
        value: eventValue,
      }
    })
  }

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters({
      ...tableFilters,
      [filterName]: {
        ...tableFilters[filterName],
        value: '',
      }
    })
  }

  const handleSearchTextRemove = ():void => {
    searchTextFormRef.current.reset();
    setSearchText('');
  }

  const handlePageSelected = (page: number):void => {
    setPageNumber(page);
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>):void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setSellersPerPage(parseInt(eventValue));
  }

  /* API CALLS */
  const getSellers = async () => {
    setLoading(true);
    cancelRequest();
    connectApi({
      type: 'get',
      url: `sellers?${filtersAsParams()}`,
      errorMessage: 'Falha ao listar varejistas'
    }).then((response) => {
      setError(false);
      let responseData = response.data;
      let properties: Property[] = responseData.properties;
      let sellers: Seller[] = responseData.sellers;
      let sellersCount:number = responseData.count;

      const propertiesCard:Property[] = [];
      properties.map((item:Property, index) => ( 
        index === properties.length-1 ?
          propertiesCard.push({
            name: item.name,
            value: item.value,
            action: () => push(BaseRoutes.sellersLeads)
          })
        : propertiesCard.push(item)
      ))

      setProperties(propertiesCard);
      setSellers(sellers);
      setSellersCount(sellersCount);
      setSellerRows([
        ...sellers.map((seller: Seller) => ({
          sellerId: seller.sellerId,
          cnpj: seller.cnpj ?? '',
          nickname: seller.nickname ?? '',
          grade: seller.grade ?? 0.0,
          seal: seller.seal ?? '',
          active: typeof (seller.active) === "boolean" ? booleanBadge(seller.active, "integratedSeller") : "-",
          isConfi: typeof (seller.isConfi) === "boolean" ? isConfiBadge(seller.isConfi) : "-",
          integratedSeller: typeof (seller.integratedSeller) === "boolean" ? booleanBadge(seller.integratedSeller, "integratedSeller") : "-",
          actions: [rowActionButton(BaseRoutes.sellerDetails.replaceAll(':id', seller.sellerId.toString()))],
        })),
      ]);
      setLoading(false);
    }).catch((error: AuthConnectionErrorProps) => {
      if (error.errorType === 'connection') {
        setError(true);
        setLoading(false);
      }
    });
  }

  return {
    loading,
    error,
    properties,
    sellerRows,
    sellersCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    sellersPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
  };
}